/**
 * Http — eigene Axios-Instanz des Editors mit Auth-Interceptors.
 * Die Interceptor-Handler sind als reine Funktionen exportiert, damit sie
 * isoliert (ohne laufenden Server) getestet werden können.
 */

import axios from 'axios'
import type { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import * as tokenStore from './tokenStore'
import { resolveBackendUrl } from '../../utils/resolveBackendUrl'

// Gleiche Baseurl-Auflösung wie der Viewer-Axios (src/utils/AxiosConfiguration.ts):
// gesetzt (auch leer) gewinnt; nur undefined fällt auf den Dev-Default durch.
const backendUrl = resolveBackendUrl(
  typeof __VITE_BACKEND_URL__ !== 'undefined' ? __VITE_BACKEND_URL__ : undefined,
  import.meta.env.VITE_BACKEND_URL,
)

// Registry für den Unauthorized-Handler. Wird von AuthContext (Task 4) gesetzt,
// um bei 401 Logout/Redirect auszulösen — ohne Zirkularimport zwischen
// http.ts und AuthContext.
let unauthorizedHandler: (() => void) | null = null

/**
 * Registriert den Handler, der bei einer 401-Antwort aufgerufen wird.
 *
 * @param fn Callback ohne Argumente
 */
export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn
}

/**
 * Liefert den aktuell registrierten Unauthorized-Handler.
 *
 * @returns registrierter Callback oder null
 */
export function getUnauthorizedHandler(): (() => void) | null {
  return unauthorizedHandler
}

/**
 * Request-Interceptor: hängt den Authorization-Header an, falls ein Token
 * im tokenStore vorhanden ist.
 *
 * @param config Axios-Request-Config
 * @returns unveränderte oder um Authorization-Header ergänzte Config
 */
export function attachAuthHeader(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const token = tokenStore.getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}

/**
 * Response-Interceptor (Erfolgsfall): entpackt die Antwort auf `response.data`.
 *
 * @param response Axios-Response
 * @returns Nutzdaten der Antwort
 */
export function unwrapResponse(response: AxiosResponse) {
  return response.data
}

/**
 * Response-Interceptor (Fehlerfall): löscht bei 401 das Token und benachrichtigt
 * den registrierten Unauthorized-Handler. Gibt den Fehler in jedem Fall
 * rejected weiter.
 *
 * @param error Axios-Fehler
 * @returns immer eine rejected Promise mit dem ursprünglichen Fehler
 */
export function handleResponseError(error: AxiosError): Promise<never> {
  if (error.response?.status === 401) {
    tokenStore.clearToken()
    getUnauthorizedHandler()?.()
  }
  return Promise.reject(error)
}

const http = axios.create({ baseURL: backendUrl })

http.interceptors.request.use(attachAuthHeader)
http.interceptors.response.use(unwrapResponse, handleResponseError)

export default http
